import { useEffect, useMemo, useState } from "react";
import { Save, SlidersHorizontal, X } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import type { FilterState } from "@/types/filters";
import {
  AWARD_OPTIONS,
  BALASAUR_BOUNDS,
  COMPLETION_OPTIONS,
  FILM_LENGTH_BUCKETS,
  IMDB_BOUNDS,
  META_BOUNDS,
  RT_BOUNDS,
  STREAMING_OPTIONS,
  YEAR_BOUNDS,
  defaultFilterState,
} from "@/types/filters";
import { UNIFIED_GENRES } from "@/lib/genres";
import { ORIGIN_OPTIONS } from "@/lib/origins";
import { AUDIENCE_OPTIONS, SUBGENRE_OPTIONS, THEME_OPTIONS } from "@/lib/taxonomy";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { searchCast, type CatalogFacets } from "@/lib/catalog.functions";
import { catalogFacetsOptions, filtersToParams } from "@/hooks/useCatalog";
import { getProviderLogos } from "@/lib/media.functions";
import { ProviderIcon, type ProviderName } from "./ProviderIcon";
import { MediaTypeSwitch, modeFromSet, setFromMode } from "./MediaTypeSwitch";
import { useAuth } from "@/hooks/useAuth";
import { useSavedFilters } from "@/hooks/useSavedFilters";
import { countActive } from "./ActiveFilters";

interface Props {
  filters: FilterState;
  setFilters: (updater: (prev: FilterState) => FilterState) => void;
  facets: CatalogFacets | undefined;
  onRequireAuth?: () => void;
}

// Order chips by count, descending, with the original list order as a stable
// tiebreaker. Used to surface the most-populated genres / origins first.
function sortByCount<T extends string>(
  options: readonly T[],
  counts: Record<string, number> | undefined,
): T[] {
  const base = [...options];
  if (!counts) return base;
  const idx = new Map<T, number>(base.map((o, i) => [o, i]));
  return base.sort(
    (a, b) => (counts[b] ?? 0) - (counts[a] ?? 0) || (idx.get(a) ?? 0) - (idx.get(b) ?? 0),
  );
}

const groupLabelClass = "text-[15px] font-black tracking-[-0.02em] text-text-bright";

const pillBase =
  "cursor-pointer select-none rounded-[4px] border px-2 py-[3px] text-[12px] font-semibold tracking-[-0.01em] transition-colors";

function Pill({
  active,
  onClick,
  children,
  disabled = false,
  count,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
  count?: number;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={
        pillBase +
        " " +
        (disabled
          ? "cursor-not-allowed border-border/50 bg-panel/40 text-text-dim"
          : active
            ? "border-primary bg-primary/15 text-primary"
            : "border-border bg-panel text-text-muted hover:border-border-strong hover:text-text-bright")
      }
    >
      {children}
      {count !== undefined && (
        <span
          className={
            "ml-1 font-mono tabular-nums " + (active ? "text-primary/70" : "text-text-dim")
          }
        >
          {count}
        </span>
      )}
    </button>
  );
}

// Collapsed-group header: shows WHAT is active ("Horror +2"), not just a dot —
// scannable without opening the group. Falls back to the dot when no summary fits.
function TriggerLabel({
  active,
  summary,
  children,
}: {
  active: boolean;
  summary?: string;
  children: React.ReactNode;
}) {
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <span className="shrink-0">{children}</span>
      {active &&
        (summary ? (
          <span className="min-w-0 truncate text-[12px] font-semibold tracking-normal text-primary">
            {summary}
          </span>
        ) : (
          <span
            className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
            aria-label="active filters"
          />
        ))}
    </span>
  );
}

/** "Horror, Thriller +2" — first couple of values plus an overflow count. */
function summarize(values: Iterable<string>, max = 2): string | undefined {
  const arr = [...values];
  if (arr.length === 0) return undefined;
  const head = arr.slice(0, max).join(", ");
  return arr.length > max ? `${head} +${arr.length - max}` : head;
}

// Decade shortcuts for the year slider — the common ask ("90s movies") in one tap.
const DECADES: { label: string; range: [number, number] }[] = [
  { label: "'70s", range: [1970, 1979] },
  { label: "'80s", range: [1980, 1989] },
  { label: "'90s", range: [1990, 1999] },
  { label: "'00s", range: [2000, 2009] },
  { label: "'10s", range: [2010, 2019] },
  { label: "'20s", range: [2020, YEAR_BOUNDS[1]] },
];

// Slider that previews locally while dragging and only COMMITS on release
// (pointer-up / key-up). Committing per drag-tick fired a query + facets
// round trip for every intermediate value — the "score filter feels broken
// for 3 seconds" bug.
function CommitSlider({
  min,
  max,
  step,
  value,
  onCommit,
}: {
  min: number;
  max: number;
  step: number;
  value: [number, number];
  onCommit: (v: [number, number]) => void;
}) {
  const [draft, setDraft] = useState<number[] | null>(null);
  return (
    <Slider
      min={min}
      max={max}
      step={step}
      value={draft ?? value}
      onValueChange={(v) => setDraft(v)}
      onValueCommit={(v) => {
        setDraft(null);
        onCommit([v[0], v[1]] as [number, number]);
      }}
    />
  );
}

// Per-category reset, shown inside a group's content only when it's active.
function GroupClear({ show, onClear }: { show: boolean; onClear: () => void }) {
  if (!show) return null;
  return (
    <button
      type="button"
      onClick={onClear}
      className="mb-2 inline-flex cursor-pointer items-center gap-1 text-[12px] font-semibold text-text-muted hover:text-text-bright"
    >
      <X className="h-2.5 w-2.5" />
      Clear
    </button>
  );
}

// "Saved" rail section: list a user's saved filter views (click to apply, × to delete)
// plus a "Save filter" CTA that appears whenever filters are active. Signed-out users
// are prompted to sign in. Fails soft via useSavedFilters (never crashes the rail).
function SavedFilters({
  filters,
  onApply,
  onRequireAuth,
}: {
  filters: FilterState;
  onApply: (f: FilterState) => void;
  onRequireAuth?: () => void;
}) {
  const { user } = useAuth();
  const { items, save, remove } = useSavedFilters();
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  const dirty = countActive(filters) > 0;

  const startSave = () => {
    if (!user) {
      onRequireAuth?.();
      return;
    }
    setName("");
    setNaming(true);
  };
  const confirmSave = async () => {
    const n = name.trim();
    if (!n) return;
    await save(n, filters);
    setName("");
    setNaming(false);
  };

  return (
    <div className="space-y-2">
      {items.length > 0 ? (
        <div className="space-y-1">
          {items.map((s) => (
            <div key={s.id} className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => onApply(s.filters)}
                title={s.name}
                className="min-w-0 flex-1 truncate rounded-[4px] border border-border bg-panel px-2 py-1 text-left text-[13px] text-text-bright hover:border-primary hover:text-primary"
              >
                {s.name}
              </button>
              <button
                type="button"
                onClick={() => remove(s.id)}
                aria-label={`Delete ${s.name}`}
                className="shrink-0 cursor-pointer p-1 text-text-dim hover:text-text-bright"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[13px] text-text-dim">
          {user ? "No saved filters yet" : "Sign in to save filters"}
        </p>
      )}

      {naming ? (
        <div className="flex items-center gap-1">
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void confirmSave();
              else if (e.key === "Escape") setNaming(false);
            }}
            placeholder="Name this view…"
            className="min-w-0 flex-1 rounded-[4px] border border-border bg-background px-2 py-1 text-[13px] text-text-bright outline-none focus:border-primary"
          />
          <button
            type="button"
            onClick={() => void confirmSave()}
            className="shrink-0 cursor-pointer rounded-[4px] bg-primary px-2 py-1 text-[13px] font-bold tracking-[-0.01em] text-primary-foreground hover:bg-primary/90"
          >
            Save
          </button>
        </div>
      ) : (
        dirty && (
          <button
            type="button"
            onClick={startSave}
            className="inline-flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-[4px] border border-primary/50 bg-primary/10 px-2 py-1.5 text-[13px] font-bold tracking-[-0.01em] text-primary hover:bg-primary/20"
          >
            <Save className="h-3 w-3" />
            Save filter
          </button>
        )
      )}
    </div>
  );
}

export function FilterRail({ filters, setFilters, facets, onRequireAuth }: Props) {
  // Origin facet stats come from a cheap cached global aggregate rather than the
  // full catalog. Counts let us grey out origins with no data; when origins haven't
  // been populated yet every count is 0 → chips disable, reading as "no origin
  // data" instead of a click landing on a blank grid.
  const genreCounts = facets?.genres ?? {};
  const originCounts = facets?.origins ?? {};
  const audienceCounts = facets?.audience ?? {};
  const filmLengthCounts = facets?.filmLength ?? {};
  const completionCounts = facets?.completion ?? {};
  // These two feed useMemos below, so keep a stable reference (a bare `?? {}` makes a
  // fresh object every render and would re-run those memos needlessly).
  const subGenreCounts = useMemo(() => facets?.subGenres ?? {}, [facets?.subGenres]);
  const themeCounts = useMemo(() => facets?.themes ?? {}, [facets?.themes]);
  const originTagged = facets?.tagged ?? 0;
  const catalogTotal = facets?.total ?? 0;

  // Which advanced facets apply to the current media-type selection.
  const showFilmLength = filters.mediaTypes.has("movie"); // movie runtime only
  const showCompletion = filters.mediaTypes.has("tv"); // series status only

  // Chip display order: sort genre/origin by OVERALL catalog popularity (the global,
  // unfiltered counts — already prefetched for the homepage, so a cache hit) rather
  // than the live faceted counts. The most-populated options sit first, but the order
  // stays put as you change other filters instead of reshuffling on every count change;
  // only the numbers on each chip update live.
  const { data: globalFacets } = useQuery(
    catalogFacetsOptions(filtersToParams(defaultFilterState())),
  );
  const genreOrder = useMemo(
    () => sortByCount(UNIFIED_GENRES, globalFacets?.genres),
    [globalFacets?.genres],
  );
  // Top eight genres by default; the expander shows the full list. Selected
  // genres outside the top eight never disappear while active.
  const [genresExpanded, setGenresExpanded] = useState(false);
  const visibleGenres = useMemo(() => {
    if (genresExpanded) return genreOrder;
    const top = genreOrder.slice(0, 8);
    for (const g of genreOrder) if (filters.genres.has(g) && !top.includes(g)) top.push(g);
    return top;
  }, [genresExpanded, genreOrder, filters.genres]);
  const originOrder = useMemo(
    () => sortByCount(ORIGIN_OPTIONS, globalFacets?.origins),
    [globalFacets?.origins],
  );
  const themeOrder = useMemo(
    () => sortByCount(THEME_OPTIONS, globalFacets?.themes),
    [globalFacets?.themes],
  );

  // Sub-genres are conditional on genre: only the ones whose parent genre is selected
  // are offered. Among those, hide ones with no results under the current filters — the
  // curated vocab is large and discovery-oriented, so (unlike the closed genre list) a
  // wall of greyed pills would be noise. Order stays stable (global popularity); only
  // the live counts and membership shift as other filters change.
  const subGenreParents = useMemo(
    () => new Map(SUBGENRE_OPTIONS.map((s) => [s.label, s.parents])),
    [],
  );
  const visibleSubGenres = useMemo(() => {
    if (filters.genres.size === 0) return [];
    const inScope = SUBGENRE_OPTIONS.filter((s) =>
      s.parents.some((p) => filters.genres.has(p)),
    ).map((s) => s.label);
    return sortByCount(inScope, globalFacets?.subGenres).filter(
      (sg) => (subGenreCounts[sg] ?? 0) > 0 || filters.subGenres.has(sg),
    );
  }, [filters.genres, filters.subGenres, subGenreCounts, globalFacets?.subGenres]);

  // Themes use the same "hide empty under current filters" rule as sub-genres.
  const visibleThemes = useMemo(
    () => themeOrder.filter((t) => (themeCounts[t] ?? 0) > 0 || filters.themes.has(t)),
    [themeOrder, themeCounts, filters.themes],
  );

  // Progressive disclosure: only surface a discovery section when it can actually do
  // something under the current filters. An active selection always keeps its section
  // visible (so a live filter is never stranded off-screen), otherwise a section with no
  // non-zero options is hidden instead of rendering an empty "nothing here" shell.
  const showSubGenre = visibleSubGenres.length > 0 || filters.subGenres.size > 0;
  const showThemes = visibleThemes.length > 0;
  const showAudience = AUDIENCE_OPTIONS.some(
    (a) => (audienceCounts[a] ?? 0) > 0 || filters.audience.has(a),
  );

  // Official provider logos (cached a day; empty → ProviderIcon shows its glyph).
  const { data: providerLogos } = useQuery({
    queryKey: ["provider-logos"],
    queryFn: () => getProviderLogos(),
    staleTime: 24 * 60 * 60 * 1000,
  });

  const toggleSet = <T,>(key: keyof FilterState, value: T) => {
    setFilters((prev) => {
      const set = new Set(prev[key] as unknown as Set<T>);
      if (set.has(value)) set.delete(value);
      else set.add(value);
      return { ...prev, [key]: set };
    });
  };

  // Genre toggle, plus prune: when a genre is removed, drop any selected sub-genre that
  // no longer has a parent genre in the set, so the conditional Sub-Genre section and
  // its chips never leave an orphaned, invisible filter behind.
  const toggleGenre = (g: string) => {
    setFilters((prev) => {
      const genres = new Set(prev.genres);
      if (genres.has(g)) genres.delete(g);
      else genres.add(g);
      let subGenres = prev.subGenres;
      if (subGenres.size > 0) {
        const next = new Set(subGenres);
        for (const sg of next) {
          const parents = subGenreParents.get(sg);
          if (parents && !parents.some((p) => genres.has(p))) next.delete(sg);
        }
        if (next.size !== subGenres.size) subGenres = next;
      }
      return { ...prev, genres, subGenres };
    });
  };

  // Per-award accolade state: each award is Off ("any"), Nominated, or Won.
  const setAward = (key: string, status: "any" | "nominated" | "won") => {
    setFilters((prev) => {
      const won = new Set(prev.awardsWon);
      const nom = new Set(prev.awardsNominated);
      won.delete(key);
      nom.delete(key);
      if (status === "won") won.add(key);
      else if (status === "nominated") nom.add(key);
      return { ...prev, awardsWon: won, awardsNominated: nom };
    });
  };

  // Reset a single filter group back to its default, leaving everything else.
  const clearGroup = (group: string) => {
    const d = defaultFilterState();
    setFilters((prev) => {
      switch (group) {
        case "media-type":
          return { ...prev, mediaTypes: d.mediaTypes };
        case "genre":
          // Clearing the genre also clears sub-genres (they hang off it).
          return { ...prev, genres: d.genres, subGenres: d.subGenres };
        case "sub-genre":
          return { ...prev, subGenres: d.subGenres };
        case "themes":
          return { ...prev, themes: d.themes };
        case "audience":
          return { ...prev, audience: d.audience };
        case "film-length":
          return { ...prev, filmLength: d.filmLength };
        case "completion":
          return { ...prev, completion: d.completion };
        case "origin":
          return { ...prev, origins: d.origins };
        case "streaming":
          return { ...prev, streaming: d.streaming };
        case "released":
          return { ...prev, yearRange: d.yearRange };
        case "score":
          return {
            ...prev,
            balasaurRange: d.balasaurRange,
            includeUnratedBalasaur: d.includeUnratedBalasaur,
          };
        case "rating":
          return {
            ...prev,
            imdbRange: d.imdbRange,
            rtRange: d.rtRange,
            metaRange: d.metaRange,
            includeUnratedImdb: d.includeUnratedImdb,
            includeUnratedRt: d.includeUnratedRt,
            includeUnratedMeta: d.includeUnratedMeta,
          };
        case "people":
          return { ...prev, people: d.people };
        case "accolades":
          return {
            ...prev,
            awardWinners: d.awardWinners,
            nominated: d.nominated,
            awardsWon: d.awardsWon,
            awardsNominated: d.awardsNominated,
          };
        default:
          return prev;
      }
    });
  };

  // Saved-views state is read here too (same hook the section uses) so the Saved
  // section can hide entirely for users with nothing saved and nothing to save.
  const { items: savedViews } = useSavedFilters();
  const totalActive = countActive(filters);

  const activeGroups = useMemo(() => {
    const s = new Set<string>();
    if (filters.mediaTypes.size !== 2) s.add("media-type");
    if (filters.genres.size > 0) s.add("genre");
    if (filters.subGenres.size > 0) s.add("sub-genre");
    if (filters.themes.size > 0) s.add("themes");
    if (filters.audience.size > 0) s.add("audience");
    if (filters.filmLength.size > 0) s.add("film-length");
    if (filters.completion.size > 0) s.add("completion");
    if (filters.origins.size > 0) s.add("origin");
    if (filters.streaming.size > 0) s.add("streaming");
    if (filters.yearRange[0] !== YEAR_BOUNDS[0] || filters.yearRange[1] !== YEAR_BOUNDS[1])
      s.add("released");
    if (
      filters.balasaurRange[0] !== BALASAUR_BOUNDS[0] ||
      filters.balasaurRange[1] !== BALASAUR_BOUNDS[1] ||
      !filters.includeUnratedBalasaur
    )
      s.add("score");
    if (
      filters.imdbRange[0] !== IMDB_BOUNDS[0] ||
      filters.imdbRange[1] !== IMDB_BOUNDS[1] ||
      filters.rtRange[0] !== RT_BOUNDS[0] ||
      filters.rtRange[1] !== RT_BOUNDS[1] ||
      filters.metaRange[0] !== META_BOUNDS[0] ||
      filters.metaRange[1] !== META_BOUNDS[1]
    )
      s.add("rating");
    if (filters.people.length > 0) s.add("people");
    if (
      filters.awardWinners ||
      filters.nominated ||
      filters.awardsWon.size > 0 ||
      filters.awardsNominated.size > 0
    )
      s.add("accolades");
    return s;
  }, [filters]);

  // Tiering: the four groups people actually reach for live at the top; the long
  // tail collapses behind one "More filters" row. Auto-expanded while any of its
  // groups is active (a live filter must never be stranded off-screen), and the
  // user's explicit toggle wins after that.
  const MORE_GROUPS = [
    "themes",
    "audience",
    "film-length",
    "completion",
    "origin",
    "rating",
    "people",
    "accolades",
  ] as const;
  const moreActiveCount = MORE_GROUPS.filter((g) => activeGroups.has(g)).length;
  const [moreToggled, setMoreToggled] = useState<boolean | null>(null);
  const moreOpen = moreToggled ?? moreActiveCount > 0;

  // Collapsed-header summaries: what's set, visible without opening the group.
  const groupSummary: Record<string, string | undefined> = {
    "media-type":
      filters.mediaTypes.size === 1
        ? filters.mediaTypes.has("movie")
          ? "Movies"
          : "TV"
        : undefined,
    streaming: summarize(filters.streaming),
    genre: summarize(filters.genres),
    "sub-genre": summarize(filters.subGenres),
    themes: summarize(filters.themes),
    audience: summarize(filters.audience),
    "film-length": summarize(filters.filmLength),
    completion: summarize(filters.completion),
    origin: summarize(filters.origins),
    released: activeGroups.has("released")
      ? `${filters.yearRange[0]}–${filters.yearRange[1]}`
      : undefined,
    score: activeGroups.has("score")
      ? `${filters.balasaurRange[0]}–${filters.balasaurRange[1]}`
      : undefined,
    rating: activeGroups.has("rating") ? "custom" : undefined,
    people: summarize(filters.people),
    accolades: activeGroups.has("accolades") ? "set" : undefined,
  };

  return (
    <div className="space-y-1">
      <Accordion
        type="multiple"
        defaultValue={["saved", "media-type", "streaming", "genre"]}
        className="w-full"
      >
        {/* Saved filters — hidden entirely until there's something saved OR an
            active filter worth saving; empty top-of-rail slots teach new users
            that the rail is mostly blank. */}
        {(savedViews.length > 0 || totalActive > 0) && (
          <AccordionItem value="saved" className="border-border">
            <AccordionTrigger className={groupLabelClass + " py-2.5"}>
              <TriggerLabel active={false}>Saved</TriggerLabel>
            </AccordionTrigger>
            <AccordionContent className="pb-3 pt-1">
              <SavedFilters
                filters={filters}
                onApply={(f) => setFilters(() => f)}
                onRequireAuth={onRequireAuth}
              />
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Media Type */}
        <AccordionItem value="media-type" className="border-border">
          <AccordionTrigger className={groupLabelClass + " py-2.5"}>
            <TriggerLabel
              active={activeGroups.has("media-type")}
              summary={groupSummary["media-type"]}
            >
              Media Type
            </TriggerLabel>
          </AccordionTrigger>
          <AccordionContent className="pb-3 pt-1">
            <MediaTypeSwitch
              mode={modeFromSet(filters.mediaTypes)}
              onChange={(m) => setFilters((prev) => ({ ...prev, mediaTypes: setFromMode(m) }))}
            />
          </AccordionContent>
        </AccordionItem>

        {/* Streaming — positioned #2, open by default */}
        <AccordionItem value="streaming" className="border-border">
          <AccordionTrigger className={groupLabelClass + " py-2.5"}>
            <TriggerLabel
              active={activeGroups.has("streaming")}
              summary={groupSummary["streaming"]}
            >
              Streaming Service
            </TriggerLabel>
          </AccordionTrigger>
          <AccordionContent className="pb-3 pt-1">
            <GroupClear
              show={activeGroups.has("streaming")}
              onClear={() => clearGroup("streaming")}
            />
            <div className="flex flex-wrap gap-2">
              {STREAMING_OPTIONS.map((s) => (
                <ProviderIcon
                  key={s}
                  provider={s as ProviderName}
                  logoUrl={providerLogos?.[s]}
                  selected={filters.streaming.has(s)}
                  onClick={() => toggleSet<string>("streaming", s)}
                  size={38}
                />
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Score — the unified Balasaur Score, the headline rating filter. The
            per-source sliders (IMDb/RT/MC) moved to Source Ratings under More.
            Sits above Genre: this is the site's differentiated filter and was
            invisible below the genre wall. */}
        <AccordionItem value="score" className="border-border">
          <AccordionTrigger className={groupLabelClass + " py-2.5"}>
            <TriggerLabel active={activeGroups.has("score")} summary={groupSummary["score"]}>
              Score
            </TriggerLabel>
          </AccordionTrigger>
          <AccordionContent className="pb-4 pt-2">
            <div className="px-1">
              <GroupClear show={activeGroups.has("score")} onClear={() => clearGroup("score")} />
              <div className="mb-1.5 flex items-center justify-between">
                <span className="font-mono text-[11px] uppercase tracking-wider text-text-muted">
                  Balasaur Score
                </span>
                <span className="font-mono text-[12px] tabular-nums text-text-bright">
                  {filters.balasaurRange[0]} – {filters.balasaurRange[1]}
                </span>
              </div>
              <CommitSlider
                min={BALASAUR_BOUNDS[0]}
                max={BALASAUR_BOUNDS[1]}
                step={1}
                value={filters.balasaurRange}
                onCommit={(v) => setFilters((prev) => ({ ...prev, balasaurRange: v }))}
              />
              <div className="mt-1.5 flex items-center justify-between gap-2">
                <label className="flex cursor-pointer items-center gap-1.5">
                  <Checkbox
                    checked={filters.includeUnratedBalasaur}
                    onCheckedChange={(v) =>
                      setFilters((prev) => ({ ...prev, includeUnratedBalasaur: !!v }))
                    }
                  />
                  <span className="text-[12px] text-text-muted">Include unrated</span>
                </label>
                <span className="font-mono text-[12px] tabular-nums text-text-dim">
                  {facets?.scored.balasaur ?? 0} of {catalogTotal} scored
                </span>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Released — above Genre for the same reason, with decade shortcuts. */}
        <AccordionItem value="released" className="border-border">
          <AccordionTrigger className={groupLabelClass + " py-2.5"}>
            <TriggerLabel active={activeGroups.has("released")} summary={groupSummary["released"]}>
              Released
            </TriggerLabel>
          </AccordionTrigger>
          <AccordionContent className="pb-4 pt-2">
            <div className="px-1">
              <GroupClear
                show={activeGroups.has("released")}
                onClear={() => clearGroup("released")}
              />
              <div className="mb-2 flex flex-wrap gap-1.5">
                {DECADES.map((d) => {
                  const active =
                    filters.yearRange[0] === d.range[0] && filters.yearRange[1] === d.range[1];
                  return (
                    <Pill
                      key={d.label}
                      active={active}
                      onClick={() =>
                        setFilters((prev) => ({
                          ...prev,
                          yearRange: active ? [...YEAR_BOUNDS] : [...d.range],
                        }))
                      }
                    >
                      {d.label}
                    </Pill>
                  );
                })}
              </div>
              <div className="mb-2 flex justify-between font-mono text-[12px] tabular-nums text-text-muted">
                <span>{filters.yearRange[0]}</span>
                <span>{filters.yearRange[1]}</span>
              </div>
              <CommitSlider
                min={YEAR_BOUNDS[0]}
                max={YEAR_BOUNDS[1]}
                step={1}
                value={filters.yearRange}
                onCommit={(v) => setFilters((prev) => ({ ...prev, yearRange: v }))}
              />
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Genre — the eight most-populated genres up front; the full list one
            tap away. Selected genres outside the top eight stay visible. */}
        <AccordionItem value="genre" className="border-border">
          <AccordionTrigger className={groupLabelClass + " py-2.5"}>
            <TriggerLabel active={activeGroups.has("genre")} summary={groupSummary["genre"]}>
              Genre
            </TriggerLabel>
          </AccordionTrigger>
          <AccordionContent className="pb-3 pt-1">
            <GroupClear show={activeGroups.has("genre")} onClear={() => clearGroup("genre")} />
            <div className="flex flex-wrap gap-1.5">
              {visibleGenres.map((g) => {
                const count = genreCounts[g] ?? 0;
                const active = filters.genres.has(g);
                return (
                  <Pill
                    key={g}
                    active={active}
                    count={count}
                    disabled={count === 0 && !active}
                    onClick={() => toggleGenre(g)}
                  >
                    {g}
                  </Pill>
                );
              })}
              <button
                type="button"
                onClick={() => setGenresExpanded(!genresExpanded)}
                aria-expanded={genresExpanded}
                className={
                  pillBase +
                  " border-dashed border-border text-text-dim hover:border-border-strong hover:text-text-bright"
                }
              >
                {genresExpanded ? "Fewer genres" : `All ${UNIFIED_GENRES.length} genres`}
              </button>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Sub-Genre — only once a parent genre is selected AND it has options to offer
            (or a live selection to keep visible), so it never renders an empty shell. */}
        {showSubGenre && (
          <AccordionItem value="sub-genre" className="border-border">
            <AccordionTrigger className={groupLabelClass + " py-2.5"}>
              <TriggerLabel
                active={activeGroups.has("sub-genre")}
                summary={groupSummary["sub-genre"]}
              >
                Sub-Genre
              </TriggerLabel>
            </AccordionTrigger>
            <AccordionContent className="pb-3 pt-1">
              <GroupClear
                show={activeGroups.has("sub-genre")}
                onClear={() => clearGroup("sub-genre")}
              />
              <div className="flex flex-wrap gap-1.5">
                {visibleSubGenres.map((sg) => {
                  const active = filters.subGenres.has(sg);
                  return (
                    <Pill
                      key={sg}
                      active={active}
                      count={subGenreCounts[sg] ?? 0}
                      onClick={() => toggleSet<string>("subGenres", sg)}
                    >
                      {sg}
                    </Pill>
                  );
                })}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* The long tail collapses behind one row, pinned to the rail's bottom
            edge so it is discoverable without scrolling to the end. */}
        <div className="sticky bottom-0 z-10 border-b border-border bg-background">
          <button
            type="button"
            onClick={() => setMoreToggled(!moreOpen)}
            aria-expanded={moreOpen}
            className={
              "flex w-full cursor-pointer items-center gap-1.5 py-2.5 " +
              groupLabelClass +
              " text-text-muted hover:text-text-bright"
            }
          >
            <SlidersHorizontal className="h-3 w-3" />
            {moreOpen ? "Fewer filters" : "More filters"}
            {moreActiveCount > 0 && (
              <span className="rounded-[3px] bg-primary/20 px-1 font-mono text-[11px] tabular-nums text-primary">
                {moreActiveCount} active
              </span>
            )}
          </button>
        </div>

        {/* Themes — hidden entirely when no theme has results under the current filters. */}
        {moreOpen && showThemes && (
          <AccordionItem value="themes" className="border-border">
            <AccordionTrigger className={groupLabelClass + " py-2.5"}>
              <TriggerLabel active={activeGroups.has("themes")} summary={groupSummary["themes"]}>
                Themes
              </TriggerLabel>
            </AccordionTrigger>
            <AccordionContent className="pb-3 pt-1">
              <GroupClear show={activeGroups.has("themes")} onClear={() => clearGroup("themes")} />
              <div className="flex flex-wrap gap-1.5">
                {visibleThemes.map((t) => {
                  const active = filters.themes.has(t);
                  return (
                    <Pill
                      key={t}
                      active={active}
                      count={themeCounts[t] ?? 0}
                      onClick={() => toggleSet<string>("themes", t)}
                    >
                      {t}
                    </Pill>
                  );
                })}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Audience — hidden when no audience bucket applies under the current filters. */}
        {moreOpen && showAudience && (
          <AccordionItem value="audience" className="border-border">
            <AccordionTrigger className={groupLabelClass + " py-2.5"}>
              <TriggerLabel
                active={activeGroups.has("audience")}
                summary={groupSummary["audience"]}
              >
                Audience
              </TriggerLabel>
            </AccordionTrigger>
            <AccordionContent className="pb-3 pt-1">
              <GroupClear
                show={activeGroups.has("audience")}
                onClear={() => clearGroup("audience")}
              />
              <div className="flex flex-wrap gap-1.5">
                {AUDIENCE_OPTIONS.map((a) => {
                  const count = audienceCounts[a] ?? 0;
                  const active = filters.audience.has(a);
                  return (
                    <Pill
                      key={a}
                      active={active}
                      count={count}
                      disabled={count === 0 && !active}
                      onClick={() => toggleSet<string>("audience", a)}
                    >
                      {a}
                    </Pill>
                  );
                })}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Film Length — movies only (runtime) */}
        {moreOpen && showFilmLength && (
          <AccordionItem value="film-length" className="border-border">
            <AccordionTrigger className={groupLabelClass + " py-2.5"}>
              <TriggerLabel
                active={activeGroups.has("film-length")}
                summary={groupSummary["film-length"]}
              >
                Film Length
              </TriggerLabel>
            </AccordionTrigger>
            <AccordionContent className="pb-3 pt-1">
              <GroupClear
                show={activeGroups.has("film-length")}
                onClear={() => clearGroup("film-length")}
              />
              <div className="flex flex-wrap gap-1.5">
                {FILM_LENGTH_BUCKETS.map((b) => {
                  const count = filmLengthCounts[b.key] ?? 0;
                  const active = filters.filmLength.has(b.key);
                  return (
                    <Pill
                      key={b.key}
                      active={active}
                      count={count}
                      disabled={count === 0 && !active}
                      onClick={() => toggleSet<string>("filmLength", b.key)}
                    >
                      {b.label}
                      <span className="ml-1 normal-case text-text-dim">{b.hint}</span>
                    </Pill>
                  );
                })}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Status — series only (completion) */}
        {moreOpen && showCompletion && (
          <AccordionItem value="completion" className="border-border">
            <AccordionTrigger className={groupLabelClass + " py-2.5"}>
              <TriggerLabel
                active={activeGroups.has("completion")}
                summary={groupSummary["completion"]}
              >
                Series Status
              </TriggerLabel>
            </AccordionTrigger>
            <AccordionContent className="pb-3 pt-1">
              <GroupClear
                show={activeGroups.has("completion")}
                onClear={() => clearGroup("completion")}
              />
              <div className="flex flex-wrap gap-1.5">
                {COMPLETION_OPTIONS.map((c) => {
                  const count = completionCounts[c] ?? 0;
                  const active = filters.completion.has(c);
                  return (
                    <Pill
                      key={c}
                      active={active}
                      count={count}
                      disabled={count === 0 && !active}
                      onClick={() => toggleSet<string>("completion", c)}
                    >
                      {c}
                    </Pill>
                  );
                })}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Origin */}
        {moreOpen && (
          <AccordionItem value="origin" className="border-border">
            <AccordionTrigger className={groupLabelClass + " py-2.5"}>
              <TriggerLabel active={activeGroups.has("origin")} summary={groupSummary["origin"]}>
                Origin
              </TriggerLabel>
            </AccordionTrigger>
            <AccordionContent className="pb-3 pt-1">
              <GroupClear show={activeGroups.has("origin")} onClear={() => clearGroup("origin")} />
              <div className="flex flex-wrap gap-1.5">
                {originOrder.map((o) => {
                  const count = originCounts[o] ?? 0;
                  const active = filters.origins.has(o);
                  return (
                    <Pill
                      key={o}
                      active={active}
                      count={count}
                      disabled={count === 0 && !active}
                      onClick={() => toggleSet<string>("origins", o)}
                    >
                      {o}
                    </Pill>
                  );
                })}
              </div>
              <div className="mt-2 font-mono text-[12px] tabular-nums text-text-dim">
                {originTagged.toLocaleString("en-US")} of {catalogTotal.toLocaleString("en-US")}{" "}
                tagged
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Source Ratings — the per-source sliders, demoted to advanced now that
            the unified Score slider covers the mainstream case. */}
        {moreOpen && (
          <AccordionItem value="rating" className="border-border">
            <AccordionTrigger className={groupLabelClass + " py-2.5"}>
              <TriggerLabel active={activeGroups.has("rating")} summary={groupSummary["rating"]}>
                Source Ratings
              </TriggerLabel>
            </AccordionTrigger>
            <AccordionContent className="pb-4 pt-2">
              <div className="px-1">
                <GroupClear
                  show={activeGroups.has("rating")}
                  onClear={() => clearGroup("rating")}
                />
              </div>
              <RatingSliders filters={filters} setFilters={setFilters} facets={facets} />
            </AccordionContent>
          </AccordionItem>
        )}

        {/* By Person */}
        {moreOpen && (
          <AccordionItem value="people" className="border-border">
            <AccordionTrigger className={groupLabelClass + " py-2.5"}>
              <TriggerLabel active={activeGroups.has("people")} summary={groupSummary["people"]}>
                By Person
              </TriggerLabel>
            </AccordionTrigger>
            <AccordionContent className="pb-3 pt-1">
              <GroupClear show={activeGroups.has("people")} onClear={() => clearGroup("people")} />
              <PeoplePicker filters={filters} setFilters={setFilters} />
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Accolades */}
        {moreOpen && (
          <AccordionItem value="accolades" className="border-border">
            <AccordionTrigger className={groupLabelClass + " py-2.5"}>
              <TriggerLabel
                active={activeGroups.has("accolades")}
                summary={groupSummary["accolades"]}
              >
                Accolades
              </TriggerLabel>
            </AccordionTrigger>
            <AccordionContent className="pb-3 pt-1">
              <GroupClear
                show={activeGroups.has("accolades")}
                onClear={() => clearGroup("accolades")}
              />
              <div className="space-y-2">
                <label className="flex cursor-pointer items-center gap-2">
                  <Checkbox
                    checked={filters.awardWinners}
                    onCheckedChange={(v) => setFilters((prev) => ({ ...prev, awardWinners: !!v }))}
                  />
                  <span className="text-[13px] text-text-bright">Award winners</span>
                </label>
                <label className="flex cursor-pointer items-center gap-2">
                  <Checkbox
                    checked={filters.nominated}
                    onCheckedChange={(v) => setFilters((prev) => ({ ...prev, nominated: !!v }))}
                  />
                  <span className="text-[13px] text-text-bright">Nominated</span>
                </label>
              </div>

              <div className="mt-3 space-y-2 border-t border-border pt-3">
                <div className="text-[13px] font-bold tracking-[-0.01em] text-text-dim">
                  By award
                </div>
                {AWARD_OPTIONS.map((a) => {
                  const status = filters.awardsWon.has(a.key)
                    ? "won"
                    : filters.awardsNominated.has(a.key)
                      ? "nominated"
                      : "any";
                  return (
                    <div key={a.key} className="flex items-center justify-between gap-2">
                      <span className="text-[12px] text-text-muted">{a.label}</span>
                      <div className="inline-flex rounded-[4px] border border-border bg-panel p-[2px]">
                        {(["any", "nominated", "won"] as const).map((s) => {
                          const active = status === s;
                          return (
                            <button
                              key={s}
                              type="button"
                              onClick={() => setAward(a.key, s)}
                              className={
                                "cursor-pointer rounded-[3px] px-2 py-[3px] text-[12px] font-semibold tracking-[-0.01em] transition-colors " +
                                (active
                                  ? "bg-accent text-text-bright"
                                  : "text-text-muted hover:text-text-bright")
                              }
                            >
                              {s === "any" ? "Any" : s === "nominated" ? "Nom." : "Won"}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>
    </div>
  );
}

function RatingSliders({
  filters,
  setFilters,
  facets,
}: {
  filters: FilterState;
  setFilters: Props["setFilters"];
  facets: CatalogFacets | undefined;
}) {
  const rows: {
    label: string;
    key: "imdbRange" | "rtRange" | "metaRange";
    includeKey: "includeUnratedImdb" | "includeUnratedRt" | "includeUnratedMeta";
    scoredKey: "imdb" | "rt" | "meta";
    bounds: [number, number];
    step: number;
    suffix?: string;
  }[] = [
    {
      label: "IMDb",
      key: "imdbRange",
      includeKey: "includeUnratedImdb",
      scoredKey: "imdb",
      bounds: IMDB_BOUNDS,
      step: 0.1,
    },
    {
      label: "Rotten Tomatoes",
      key: "rtRange",
      includeKey: "includeUnratedRt",
      scoredKey: "rt",
      bounds: RT_BOUNDS,
      step: 1,
      suffix: "%",
    },
    {
      label: "Metacritic",
      key: "metaRange",
      includeKey: "includeUnratedMeta",
      scoredKey: "meta",
      bounds: META_BOUNDS,
      step: 1,
    },
  ];

  const total = facets?.total ?? 0;

  return (
    <div className="space-y-4 px-1">
      {rows.map((row) => {
        const value = filters[row.key];
        const covered = facets?.scored[row.scoredKey] ?? 0;
        return (
          <div key={row.key}>
            <div className="mb-1.5 flex items-center justify-between">
              <span className="font-mono text-[11px] uppercase tracking-wider text-text-muted">
                {row.label}
              </span>
              <span className="font-mono text-[12px] tabular-nums text-text-bright">
                {value[0]}
                {row.suffix ?? ""} – {value[1]}
                {row.suffix ?? ""}
              </span>
            </div>
            <CommitSlider
              min={row.bounds[0]}
              max={row.bounds[1]}
              step={row.step}
              value={value}
              onCommit={(v) => setFilters((prev) => ({ ...prev, [row.key]: v }))}
            />
            <div className="mt-1.5 flex items-center justify-between gap-2">
              <label className="flex cursor-pointer items-center gap-1.5">
                <Checkbox
                  checked={filters[row.includeKey]}
                  onCheckedChange={(v) =>
                    setFilters((prev) => ({ ...prev, [row.includeKey]: !!v }))
                  }
                />
                <span className="text-[12px] text-text-muted">Include unrated</span>
              </label>
              <span className="font-mono text-[12px] tabular-nums text-text-dim">
                {covered} of {total} scored
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PeoplePicker({
  filters,
  setFilters,
}: {
  filters: FilterState;
  setFilters: Props["setFilters"];
}) {
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState<string[]>([]);
  const search = useServerFn(searchCast);

  // Debounced server-side cast/crew search (replaces scanning the in-memory catalog).
  useEffect(() => {
    const qq = query.trim();
    if (!qq) {
      setMatches([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await search({ data: { query: qq, exclude: filters.people } });
        if (!cancelled) setMatches(res);
      } catch {
        if (!cancelled) setMatches([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, filters.people, search]);

  const add = (name: string) => {
    setFilters((prev) =>
      prev.people.includes(name) ? prev : { ...prev, people: [...prev.people, name] },
    );
    setQuery("");
  };

  const remove = (name: string) => {
    setFilters((prev) => ({ ...prev, people: prev.people.filter((p) => p !== name) }));
  };

  return (
    <div className="space-y-2">
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search a name…"
        className="h-8 w-full rounded-[4px] border border-border bg-panel px-2 text-[13px] text-foreground placeholder:text-text-dim focus:border-border-strong focus:outline-none"
      />
      {matches.length > 0 && (
        <ul className="rounded-[4px] border border-border bg-panel">
          {matches.map((name) => (
            <li key={name}>
              <button
                type="button"
                onClick={() => add(name)}
                className="block w-full cursor-pointer px-2 py-1 text-left text-[13px] text-text-bright hover:bg-accent"
              >
                {name}
              </button>
            </li>
          ))}
        </ul>
      )}
      {filters.people.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {filters.people.map((p) => (
            <span
              key={p}
              className="inline-flex items-center gap-1 rounded-[4px] border border-primary bg-primary/15 px-1.5 py-[2px] text-[12px] font-semibold text-primary"
            >
              {p}
              <button
                type="button"
                onClick={() => remove(p)}
                className="cursor-pointer hover:text-text-bright"
                aria-label={`Remove ${p}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
